'use client';

import React, { useCallback, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { colors } from '@startengine/design-system.theme.main';
import { Button } from '@startengine/design-system.ui.button';
import { useParams, useSearchParams, usePathname } from 'next/navigation';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';

// Import all the required hooks and utilities (business logic stays intact)
import useLoginModal from 'hooks/useLoginModal';
import { getOffering } from 'services/offerings';
import {
  investNowClick,
  trackEvent,
  trackSEEvent,
  appendPostHogDistinctId,
  ADD_TO_CART_SURFACES,
} from 'utils/analytics';
import { INVESTOR_SIGNALS_BEHAVIORAL_EVENTS } from 'utils/investorSignalsBehavioralProperties';
import { userMenu } from 'utils/links';
import { LEGACY_RESERVATION_LIST } from 'utils/constants/strings';
import { getEnv, hasUserSeIraAccount, isRetirementFundsOnly } from 'utils/helpers';
import { isOverAmountRaised, resolveOfferingInvestCtaText } from 'utils/offerings/helpers';
import useCanadianFeatureFlag from 'hooks/useCanadianFeatureFlag';

import { useBuilderABTest } from '../BuilderABTestContext';
import { RADIUS, SPACING, TYPOGRAPHY } from '../designTokens';

// Defaults below are mirrored in the Builder.io component schema, so they
// are intentionally explicit (not tokenized) — changing the defaults here
// also changes what content editors see as the starting state.
const StyledInvestButton = styled(Button)(({ theme, customStyles }) => ({
  ...theme.typography.p2,
  fontFamily: 'var(--builder-body-font, inherit)',
  borderRadius: customStyles?.borderRadius || RADIUS.xl,
  width: customStyles?.width || '100%',
  maxWidth: customStyles?.maxWidth || '327px',
  fontWeight: customStyles?.fontWeight || 600,
  fontSize: customStyles?.fontSize || '20px',
  display: 'flex',
  flexDirection: 'column',
  padding: customStyles?.padding || `${SPACING.sm} ${SPACING.xl}`,
  backgroundColor: customStyles?.backgroundColor || '#F47F2B',
  color: customStyles?.textColor || colors.white,
  '&:hover': {
    backgroundColor:
      customStyles?.hoverBackgroundColor || customStyles?.backgroundColor || '#E06F1B',
    opacity: 0.9,
  },
  '&:disabled': {
    backgroundColor: colors.gray4,
    color: colors.white,
  },
}));

const InvestBox = styled(Box)(() => ({
  fontSize: TYPOGRAPHY.caption.fontSize,
  fontWeight: '500',
  color: colors.white,
  marginTop: SPACING.xxs,
}));

const IraDisclaimer = styled(Typography)(() => ({
  fontSize: '10px',
  fontWeight: 400,
  marginTop: SPACING.xs,
  textAlign: 'center',
  color: colors.gray1,
  lineHeight: '1.3',
  maxWidth: '327px',
  a: {
    color: colors.SEDark,
    textDecoration: 'underline',
  },
}));

/**
 * BuilderInvestButton - A Builder.io compatible Invest Button
 *
 * This component wraps all the complex investment logic while exposing
 * only safe, visual customization options to content editors.
 *
 * LOCKED (Not editable in Builder.io):
 * - Authentication flow
 * - Investment account validation
 * - URL routing and redirects
 * - Analytics tracking
 * - Offering status handling
 * - IRA-only logic
 *
 * CUSTOMIZABLE (Editable in Builder.io):
 * - Button text override
 * - Button colors
 * - Border radius
 * - Font size/weight
 * - Additional promotional text
 * - Show/hide price per share
 */
const BuilderInvestButton = ({
  // Customizable props (exposed to Builder.io)
  buttonTextOverride,
  buttonText,
  waitlistTextOverride,
  waitlistText,
  backgroundColor,
  hoverBackgroundColor,
  textColor,
  borderRadius,
  fontSize,
  fontWeight,
  padding,
  maxWidth,
  width,
  showPricePerShare,
  showIraDisclaimer,
  additionalText,
  additionalTextColor,
  // Data context (passed from parent)
  offeringSlug,
  sectionName,
}) => {
  const params = useParams();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const abTest = useBuilderABTest();

  // Derive slug from props or params
  const slug = offeringSlug || params?.slug?.[0];

  // Canadian investor flow
  const { isEnabled: isCanadianFeatureEnabled } = useCanadianFeatureFlag();
  const isCanadianRoute = isCanadianFeatureEnabled && pathname?.startsWith('/private-canada/');

  // Fetch offering data
  const { data: offeringData } = useQuery({
    queryKey: ['offeringData', slug],
    queryFn: () => getOffering({ slug }),
    staleTime: 15 * 60 * 1000,
    refetchOnWindowFocus: true,
    enabled: Boolean(slug),
    placeholderData: (prev) => prev,
  });

  // Redux state
  const user = useSelector((state) => state.user);
  const userAccounts = useSelector((state) => state.user.userAccounts);
  const investmentAccountStatus = useSelector((state) => state.user.investmentAccountStatus);
  const {
    needToLogin,
    verifiedPrimaryUserId,
    loading: partnerLoading,
  } = useSelector((state) => state.partnerVerification);

  // Hooks
  const [renderLoginModal, setLoginModalOpen] = useLoginModal();

  // Local state
  const [url, setUrl] = useState('');

  // Computed values
  const isOverRaised = isOverAmountRaised(offeringData) || offeringData?.lockedOnWaitlist;
  const partner = searchParams?.get('partner');
  const connectRequestId = searchParams?.get('connectRequestId')?.replaceAll('/', '');
  const convertibleNote = offeringData?.offeringType === 'convertible_note';
  const simpleAgreementForFutureEquity = offeringData?.offeringType === 'safe';

  // Determine button text - allow override from Builder.io
  const CTAText = useMemo(() => {
    if (isOverRaised) {
      return waitlistTextOverride || waitlistText || 'Join the Waitlist';
    }
    return resolveOfferingInvestCtaText({
      offeringData,
      configuredText: buttonTextOverride || buttonText,
    });
  }, [
    buttonText,
    buttonTextOverride,
    waitlistText,
    waitlistTextOverride,
    isOverRaised,
    offeringData,
  ]);

  // Custom styles from Builder.io props
  const customStyles = useMemo(
    () => ({
      backgroundColor: isOverRaised ? colors.SEOrange : backgroundColor || '#F47F2B',
      hoverBackgroundColor: isOverRaised
        ? colors.SEOrange
        : hoverBackgroundColor || backgroundColor || '#E06F1B',
      textColor: textColor || colors.white,
      borderRadius: borderRadius || '16px',
      fontSize: fontSize || '20px',
      fontWeight: fontWeight || 600,
      padding: padding || '12px 24px',
      maxWidth: maxWidth || '327px',
      width: width || '100%',
    }),
    [
      isOverRaised,
      backgroundColor,
      hoverBackgroundColor,
      textColor,
      borderRadius,
      fontSize,
      fontWeight,
      padding,
      maxWidth,
      width,
    ],
  );

  // URL generation (business logic - NOT editable)
  const getRerouteUrl = useCallback(
    (isReserved) => {
      if (investmentAccountStatus?.investmentAccountFrozen) {
        return userMenu.portfolio;
      }

      let url = '';

      if (LEGACY_RESERVATION_LIST.includes(offeringData?.slug)) {
        url = `${process.env.NEXT_PUBLIC_ROOT_URL}/${slug}/reserve`;
      } else if (isReserved) {
        url = `${getEnv()}investment.startengine.com/${slug}/reserve`;
      } else if (
        typeof window !== 'undefined' &&
        window?.location?.hash === '#enforceInvestmentAccount'
      ) {
        url = `${getEnv()}investment.startengine.com/${slug}?action=enforceInvestmentAccount`;
      } else {
        url = `${getEnv()}investment.startengine.com/${slug}`;
      }

      if (partner && connectRequestId) {
        url = `${url}${url.includes('?') ? '&' : '?'}partner=${partner}&connectRequestId=${connectRequestId}`;
      }

      return url;
    },
    [
      offeringData?.slug,
      slug,
      partner,
      connectRequestId,
      investmentAccountStatus?.investmentAccountFrozen,
    ],
  );

  // Click handler (business logic - NOT editable)
  const handleClick = useCallback(
    async (isReserved) => {
      if (isCanadianRoute) {
        window.location.href = `/private-canada/invest?offering=${slug}`;
        return;
      }

      if (investmentAccountStatus?.investmentAccountFrozen) {
        window.location.href = userMenu.portfolio;
        return;
      }

      const url = getRerouteUrl(isReserved);

      if (offeringData) {
        investNowClick({
          offeringData,
          user,
          surface: ADD_TO_CART_SURFACES.CAMPAIGN_PAGE,
        });
        trackSEEvent(
          INVESTOR_SIGNALS_BEHAVIORAL_EVENTS.clickInvestNow,
          offeringData?.slug,
          user,
        ).catch(() => {});
        trackEvent(INVESTOR_SIGNALS_BEHAVIORAL_EVENTS.clickInvestNow, offeringData).catch(() => {});
        abTest?.trackInvestClick({ offering_slug: slug, section_name: sectionName });
      }

      const isAuthenticated = !!user?.signedIn;

      trackEvent('invest_auth_redirect_initiated', {
        source: 'invest_now_button',
        offeringSlug: offeringData?.slug,
        isAuthenticated,
        redirectUrl: url,
      });

      if (!isAuthenticated || (needToLogin && !partnerLoading)) {
        setUrl(url);
        setLoginModalOpen(true);
        return;
      }

      if (typeof window !== 'undefined') {
        const fullUrl = `${url}${window.location.search || ''}`;
        window.location.href = appendPostHogDistinctId(fullUrl);
      }
    },
    [
      offeringData,
      user,
      setLoginModalOpen,
      needToLogin,
      partnerLoading,
      getRerouteUrl,
      investmentAccountStatus,
      slug,
      isCanadianRoute,
      abTest,
      sectionName,
    ],
  );

  const onCTAClick = useCallback(async () => {
    if (offeringData?.status === 'not_submitted') {
      return null;
    }

    if (investmentAccountStatus?.investmentAccountFrozen) {
      window.location.href = userMenu.portfolio;
      return null;
    }

    await handleClick(false);
    return null;
  }, [offeringData, handleClick, investmentAccountStatus?.investmentAccountFrozen]);

  // Render price per share info
  const renderPriceInfo = () => {
    if (!showPricePerShare) return null;

    if (simpleAgreementForFutureEquity && offeringData?.discountRate) {
      return <InvestBox>{offeringData.discountRate}% Discount</InvestBox>;
    }

    if (convertibleNote && offeringData?.debtInterestRate) {
      return (
        <InvestBox>
          {Number.parseFloat(offeringData.debtInterestRate).toFixed(1)}% Interest Rate
        </InvestBox>
      );
    }

    if (offeringData?.pricePerShare) {
      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
      });
      return (
        <InvestBox>
          {formatter.format(Number.parseFloat(offeringData.pricePerShare))} Per Share
        </InvestBox>
      );
    }

    return null;
  };

  // Don't render if offering not loaded or certain conditions
  const showButton =
    offeringData &&
    (offeringData.status === 'approved' ||
      offeringData.status === 'pending' ||
      offeringData.status === 'not_submitted');

  if (offeringData?.status === 'closed') {
    return (
      <StyledInvestButton
        disabled
        className="startengine-button"
        customStyles={{ ...customStyles, backgroundColor: colors.gray4 }}
        data-cy="builder-invest-button-closed"
      >
        Offering Closed
      </StyledInvestButton>
    );
  }

  if (offeringData?.status === 'testing') {
    return (
      <StyledInvestButton
        onClick={() => handleClick(true)}
        className="startengine-button"
        customStyles={customStyles}
        data-cy="builder-reserve-button"
      >
        {CTAText}
      </StyledInvestButton>
    );
  }

  if (!showButton) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <StyledInvestButton
        disabled={offeringData?.status === 'not_submitted'}
        onClick={onCTAClick}
        className="startengine-button"
        customStyles={customStyles}
        data-cy="builder-invest-button"
      >
        {CTAText}
        {renderPriceInfo()}
      </StyledInvestButton>

      {/* Additional promotional text (customizable) */}
      {additionalText && (
        <Typography
          sx={{
            marginTop: '8px',
            fontSize: '14px',
            fontWeight: 600,
            color: additionalTextColor || colors.SEDark,
            textAlign: 'center',
          }}
        >
          {additionalText}
        </Typography>
      )}

      {/* IRA disclaimer (business logic determines visibility, but can be toggled) */}
      {showIraDisclaimer &&
        isRetirementFundsOnly(offeringData) &&
        !hasUserSeIraAccount(userAccounts) && (
          <IraDisclaimer>
            Equity Trust Company is the custodian of StartEngine IRAs. StartEngine Inc. covers IRA
            account fees if you invest $20,000 or more per year; otherwise, you are responsible for
            all applicable fees annually. See{' '}
            <a
              href="https://s3.us-east-2.amazonaws.com/forms.equitytrustcompany.info/WealthBridge_Fee-Schedule.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              IRA Fees
            </a>{' '}
            for details.
          </IraDisclaimer>
        )}

      {/* Login modal - business logic, not editable */}
      {renderLoginModal({
        offering: offeringData,
        verifiedPrimaryUserId,
        onClose: (user) => {
          if (user?.event === 'signup' && typeof window !== 'undefined') {
            const search = window.location.search || '';
            window.location.href = appendPostHogDistinctId(`${url}${search}`);
          }
        },
        onSuccessfulLogin: (response) => {
          if (response?.error) return;

          const pageType = offeringData?.status === 'testing' ? 'reserve page' : 'invest page';

          if (typeof window !== 'undefined') {
            window.dataLayer?.push({
              event: 'investNowClick',
              id: offeringData?.slug,
              name: offeringData?.name,
              category: offeringData?.regulationType,
              pageType,
              customer_id: user?.primaryUserId,
              ecommerce: {
                items: [
                  {
                    item_id: offeringData?.slug,
                    item_name: offeringData?.name,
                    item_category: offeringData?.regulationType,
                    price: offeringData?.pricePerShare
                      ? parseFloat(offeringData.pricePerShare)
                      : undefined,
                    quantity: 1,
                    currency: 'USD',
                  },
                ],
              },
            });

            setTimeout(() => {
              const search = window.location.search || '';
              window.location.href = appendPostHogDistinctId(`${url}${search}`);
            }, 250);
          }
        },
      })}
    </Box>
  );
};

BuilderInvestButton.propTypes = {
  // Customizable props (exposed to Builder.io)
  buttonTextOverride: PropTypes.string,
  waitlistTextOverride: PropTypes.string,
  backgroundColor: PropTypes.string,
  hoverBackgroundColor: PropTypes.string,
  textColor: PropTypes.string,
  borderRadius: PropTypes.string,
  fontSize: PropTypes.string,
  fontWeight: PropTypes.number,
  padding: PropTypes.string,
  maxWidth: PropTypes.string,
  width: PropTypes.string,
  showPricePerShare: PropTypes.bool,
  showIraDisclaimer: PropTypes.bool,
  additionalText: PropTypes.string,
  additionalTextColor: PropTypes.string,
  // Data context
  offeringSlug: PropTypes.string,
  sectionName: PropTypes.string,
};

BuilderInvestButton.defaultProps = {
  buttonTextOverride: '',
  waitlistTextOverride: '',
  backgroundColor: '',
  hoverBackgroundColor: '',
  textColor: '',
  borderRadius: '16px',
  fontSize: '20px',
  fontWeight: 600,
  padding: '12px 24px',
  maxWidth: '327px',
  width: '100%',
  showPricePerShare: true,
  showIraDisclaimer: true,
  additionalText: '',
  additionalTextColor: '',
  offeringSlug: '',
  sectionName: '',
};

export default BuilderInvestButton;
